import { useMemo, useState } from "react";
import {
  GiBugleCall,
  GiBreakingChain,
  GiCheckMark,
  GiCrown,
  GiMagnifyingGlass,
} from "react-icons/gi";
import { useCastle } from "../api/castle";
import { useMe, useUnitCatalog } from "../api/army";
import { useHeroes } from "../api/heroes";
import {
  useAlliances,
  useAllianceBuildings,
  useAllianceContributors,
  useAllianceResearch,
  useContributeToAlliance,
  useCreateAlliance,
  useDisbandAlliance,
  useJoinAlliance,
  useKickFromAlliance,
  useLeaveAlliance,
  useMyAlliance,
  useStartAllianceBuilding,
  useStartAllianceResearch,
  type AllianceBuildingsResponse,
  type AllianceContributor,
  type AllianceResearchResponse,
  type AllianceResearchTreeView,
} from "../api/alliance";
import {
  useActiveRallies,
  useCancelRally,
  useJoinRally,
  useRallyCamps,
  useRallyDetails,
  useStartRally,
} from "../api/rally";
import { Loading } from "./Loading";
import { ResourceBar } from "../components/ResourceBar";
import { ScreenBanner } from "../components/ScreenBanner";
import { TierShield } from "../components/TierShield";
import { CurrencyValue } from "../components/CurrencyIcon";
import { ElementRu } from "../types/army";
import { useNow } from "../lib/ticker";
import { formatDuration, formatNumber, secondsUntil } from "../lib/format";
import { hapticImpact, hapticNotification } from "../lib/telegram";
import { RallyCampEntry } from "../types/rally";
import { ElementEmblem } from "../components/ElementEmblem";
import { PveComposer } from "../components/PveComposer";

type View =
  | "home"
  | "directory"
  | "create"
  | "rally"
  | "rally-start"
  | "rally-join"
  | "rally-details"
  | "research"
  | "contribute"
  | "contributors"
  | "buildings";

const TRAVEL_BASE_SECONDS = 30;

interface Props {
  // When true, skip the banner + resource bar so this can be embedded as a
  // sub-tab inside a screen that already shows them (e.g. MeScreen).
  embedded?: boolean;
}

export function AllianceScreen({ embedded = false }: Props = {}) {
  const castle = useCastle();
  const heroes = useHeroes();
  const me = useMe();
  const units = useUnitCatalog();
  const myAlliance = useMyAlliance();
  const alliancesList = useAlliances(true);
  const activeRallies = useActiveRallies();

  const create = useCreateAlliance();
  const join = useJoinAlliance();
  const leave = useLeaveAlliance();
  const kick = useKickFromAlliance();
  const disband = useDisbandAlliance();

  const rallyCamps = useRallyCamps(true);
  const startRally = useStartRally();
  const joinRally = useJoinRally();
  const cancelRally = useCancelRally();

  // Alliance economy — research catalog + treasury + contributors. Queries
  // only fire when the player is actually in an alliance (myAlliance.data?.inAlliance).
  const inAlliance = myAlliance.data?.inAlliance === true;
  const allianceResearch = useAllianceResearch(inAlliance);
  const allianceContributors = useAllianceContributors(inAlliance);
  const allianceBuildings = useAllianceBuildings(inAlliance);
  const startAllianceResearch = useStartAllianceResearch();
  const startAllianceBuilding = useStartAllianceBuilding();
  const contributeToAlliance = useContributeToAlliance();

  const [view, setView] = useState<View>("home");
  const [selectedCamp, setSelectedCamp] = useState<RallyCampEntry | null>(null);
  const [openRallyId, setOpenRallyId] = useState<string | null>(null);
  // Leader picks how long allies have to join. Server whitelists the same
  // four values; default 5m as a sensible middle ground.
  const [gatheringSeconds, setGatheringSeconds] = useState<number>(300);
  const rallyDetails = useRallyDetails(openRallyId);

  const [createForm, setCreateForm] = useState({ tag: "", name: "", description: "" });

  const now = useNow(1000);

  const unitsByIdMap = useMemo(() => {
    const m = new Map<string, NonNullable<typeof units.data>[number]>();
    units.data?.forEach((u) => m.set(u.id, u));
    return m;
  }, [units.data]);

  if (castle.isPending || me.isPending || myAlliance.isPending) {
    return <Loading message="Альянсы зовут..." />;
  }

  const data = castle.data!;
  const meData = me.data!;
  const mine = myAlliance.data;
  const errorMsg =
    pickError(create.error) ??
    pickError(join.error) ??
    pickError(leave.error) ??
    pickError(kick.error) ??
    pickError(disband.error) ??
    pickError(startRally.error) ??
    pickError(joinRally.error) ??
    pickError(cancelRally.error);

  return (
    <div className="flex h-full flex-col">
      {!embedded && (
        <>
          <ScreenBanner kind="alliance" title="Альянс" />
          <ResourceBar resources={data.state.resources} safeStorage={data.safeStorage} />
        </>
      )}

      <div className="flex-1 overflow-hidden">
        {view === "home" &&
          (mine?.inAlliance && mine.alliance ? (
            <InAllianceHome
              tag={mine.alliance.tag}
              name={mine.alliance.name}
              description={mine.alliance.description}
              leaderId={mine.alliance.leaderId}
              myId={meData.id}
              members={mine.members ?? []}
              onLeave={() => leave.mutate()}
              onKick={(playerId) =>
                kick.mutate({ allianceId: mine.alliance!.id, playerId })
              }
              onDisband={() => disband.mutate(mine.alliance!.id)}
              onOpenRally={() => setView("rally")}
              onOpenResearch={() => setView("research")}
              onOpenContribute={() => setView("contribute")}
              onOpenContributors={() => setView("contributors")}
              onOpenBuildings={() => setView("buildings")}
              errorMsg={errorMsg}
            />
          ) : (
            <NoAllianceHome
              onOpenDirectory={() => setView("directory")}
              onOpenCreate={() => setView("create")}
            />
          ))}

        {view === "directory" && (
          <DirectoryView
            alliances={alliancesList.data ?? []}
            onBack={() => setView("home")}
            onJoin={(id) => {
              hapticImpact("medium");
              join.mutate(id, {
                onSuccess: () => {
                  hapticNotification("success");
                  setView("home");
                },
              });
            }}
            joinPending={join.isPending}
            errorMsg={errorMsg}
          />
        )}

        {view === "create" && (
          <CreateAllianceForm
            value={createForm}
            onChange={setCreateForm}
            onBack={() => setView("home")}
            onSubmit={() => {
              hapticImpact("medium");
              create.mutate(createForm, {
                onSuccess: () => {
                  setCreateForm({ tag: "", name: "", description: "" });
                  setView("home");
                },
              });
            }}
            pending={create.isPending}
            errorMsg={errorMsg}
          />
        )}

        {view === "rally" && (
          <RallyHome
            activeRallies={activeRallies.data ?? []}
            camps={rallyCamps.data ?? []}
            now={now}
            onBack={() => setView("home")}
            onPickCamp={(c) => {
              setSelectedCamp(c);
              startRally.reset();
              setView("rally-start");
            }}
            onPickRally={(id) => {
              setOpenRallyId(id);
              setView("rally-details");
            }}
            errorMsg={errorMsg}
          />
        )}

        {view === "research" && (
          <AllianceResearchView
            data={allianceResearch.data}
            isLoading={allianceResearch.isPending}
            isLeader={mine?.alliance?.leaderId === meData.id}
            now={now}
            onBack={() => setView("home")}
            onStart={(id) => {
              hapticImpact("medium");
              startAllianceResearch.mutate(id, {
                onSuccess: () => hapticNotification("success"),
              });
            }}
            startPending={startAllianceResearch.isPending}
            startError={pickError(startAllianceResearch.error)}
          />
        )}

        {view === "contribute" && (
          <AllianceContributeView
            resources={data.state.resources}
            treasury={allianceResearch.data?.treasury}
            onBack={() => setView("home")}
            onSubmit={(amounts) => {
              hapticImpact("medium");
              contributeToAlliance.mutate(amounts, {
                onSuccess: () => {
                  hapticNotification("success");
                  setView("home");
                },
              });
            }}
            pending={contributeToAlliance.isPending}
            errorMsg={pickError(contributeToAlliance.error)}
          />
        )}

        {view === "contributors" && (
          <AllianceContributorsView
            contributors={allianceContributors.data ?? []}
            onBack={() => setView("home")}
          />
        )}

        {view === "buildings" && (
          <AllianceBuildingsView
            data={allianceBuildings.data}
            isLoading={allianceBuildings.isPending}
            isLeader={mine?.alliance?.leaderId === meData.id}
            now={now}
            onBack={() => setView("home")}
            onStart={(id) => {
              hapticImpact("medium");
              startAllianceBuilding.mutate(id, {
                onSuccess: () => hapticNotification("success"),
              });
            }}
            startPending={startAllianceBuilding.isPending}
            startError={pickError(startAllianceBuilding.error)}
          />
        )}

        {view === "rally-start" && selectedCamp && (
          <PveComposer
            camp={{
              id: selectedCamp.id,
              name: selectedCamp.name,
              description: selectedCamp.description,
              tier: selectedCamp.tier,
              garrison: selectedCamp.garrison,
              loot: selectedCamp.loot,
              heroXp: selectedCamp.heroXp,
              cooldownSeconds: selectedCamp.cooldownSeconds,
              nextAttackAt: selectedCamp.nextAttackAt,
              onCooldown: selectedCamp.onCooldown,
            }}
            garrison={data.state.garrison}
            unitsById={unitsByIdMap}
            ownedHeroes={(heroes.data ?? []).filter((h) => h.owned)}
            marchCapacity={data.marchCapacity}
            marchSpeedReductionPct={data.researchBonuses.marchSpeed ?? 0}
            travelBaseSeconds={TRAVEL_BASE_SECONDS}
            confirmLabel={<><GiBugleCall size={16} /> Объявить сбор</>}
            extraSection={
              <RallyGatheringPicker
                value={gatheringSeconds}
                onChange={setGatheringSeconds}
              />
            }
            onBack={() => setView("rally")}
            onConfirm={(unitsPicked, heroIds, _stratagem) =>
              startRally.mutate(
                { campId: selectedCamp.id, units: unitsPicked, heroIds, gatheringSeconds },
                {
                  onSuccess: () => {
                    setSelectedCamp(null);
                    setView("rally");
                  },
                },
              )
            }
            pending={startRally.isPending}
            error={errorMsg}
          />
        )}

        {view === "rally-details" && openRallyId && (
          <RallyDetailsView
            details={rallyDetails.data ?? null}
            now={now}
            myId={meData.id}
            onBack={() => {
              setOpenRallyId(null);
              setView("rally");
            }}
            onCancel={() => {
              cancelRally.mutate(openRallyId, {
                onSuccess: () => {
                  setOpenRallyId(null);
                  setView("rally");
                },
              });
            }}
            onJoin={() => setView("rally-join")}
            errorMsg={errorMsg}
          />
        )}

        {view === "rally-join" && rallyDetails.data && (
          <PveComposer
            camp={{
              id: rallyDetails.data.campId,
              name: rallyDetails.data.campName ?? "Rally",
              description: "Присоединиться к ралли альянса",
              tier: 5,
              garrison: [],
              loot: { gold: 0, wood: 0, stone: 0, mana: 0 },
              heroXp: 0,
              cooldownSeconds: 0,
              nextAttackAt: null,
              onCooldown: false,
            }}
            garrison={data.state.garrison}
            unitsById={unitsByIdMap}
            ownedHeroes={(heroes.data ?? []).filter((h) => h.owned)}
            marchCapacity={data.marchCapacity}
            marchSpeedReductionPct={data.researchBonuses.marchSpeed ?? 0}
            travelBaseSeconds={TRAVEL_BASE_SECONDS}
            onBack={() => setView("rally-details")}
            onConfirm={(unitsPicked, heroIds, _stratagem) =>
              joinRally.mutate(
                { rallyId: rallyDetails.data!.id, units: unitsPicked, heroIds },
                {
                  onSuccess: () => setView("rally-details"),
                },
              )
            }
            pending={joinRally.isPending}
            error={errorMsg}
          />
        )}
      </div>
    </div>
  );
}

// Four-button picker for the rally gathering window. Must match the server
// whitelist in RallyGatheringWindows.AllowedSeconds — keep both in sync.
function RallyGatheringPicker({
  value,
  onChange,
}: {
  value: number;
  onChange: (seconds: number) => void;
}) {
  const options: Array<{ s: number; label: string }> = [
    { s: 60,       label: "1 мин" },
    { s: 5 * 60,   label: "5 мин" },
    { s: 15 * 60,  label: "15 мин" },
    { s: 30 * 60,  label: "30 мин" },
  ];
  return (
    <div className="rounded-xl bg-tg-secondaryBg p-3">
      <div className="mb-2 text-xs uppercase tracking-wide text-tg-hint">
        Окно сбора (сколько ждать союзников)
      </div>
      <div className="grid grid-cols-4 gap-1.5">
        {options.map((o) => (
          <button
            key={o.s}
            type="button"
            onClick={() => onChange(o.s)}
            className={`rounded-lg px-2 py-2 text-sm ${
              value === o.s
                ? "bg-tg-button text-tg-buttonText"
                : "bg-tg-bg text-tg-text"
            }`}
          >
            {o.label}
          </button>
        ))}
      </div>
      <div className="mt-2 text-[11px] text-tg-hint leading-snug">
        Если за это время к тебе не присоединится хотя бы 1 союзник, ралли
        отменится и войска вернутся в гарнизоны.
      </div>
    </div>
  );
}

function NoAllianceHome({
  onOpenDirectory,
  onOpenCreate,
}: {
  onOpenDirectory: () => void;
  onOpenCreate: () => void;
}) {
  return (
    <div className="flex h-full flex-col p-3">
      <div className="rounded-xl bg-tg-secondaryBg p-4 text-sm">
        <div className="mb-1 text-lg font-semibold">Ты не в альянсе</div>
        <div className="text-tg-hint">
          Альянс открывает ралли на боссов и общую координацию. Вступай в существующий или создай свой.
        </div>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2">
        <button
          type="button"
          onClick={onOpenDirectory}
          className="rounded-lg bg-tg-button px-4 py-3 font-medium text-tg-buttonText"
        >
          <span className="inline-flex items-center justify-center gap-1.5"><GiMagnifyingGlass size={14} /> Найти</span>
        </button>
        <button
          type="button"
          onClick={onOpenCreate}
          className="rounded-lg bg-tg-secondaryBg px-4 py-3 font-medium"
        >
          <span className="inline-flex items-center justify-center gap-1.5"><span className="text-base leading-none">+</span> Создать</span>
        </button>
      </div>
    </div>
  );
}

function InAllianceHome({
  tag,
  name,
  description,
  leaderId,
  myId,
  members,
  onLeave,
  onKick,
  onDisband,
  onOpenRally,
  onOpenResearch,
  onOpenContribute,
  onOpenContributors,
  onOpenBuildings,
  errorMsg,
}: {
  tag: string;
  name: string;
  description: string;
  leaderId: string;
  myId: string;
  members: { playerId: string; displayName: string; element: number; role: string }[];
  onLeave: () => void;
  onKick: (playerId: string) => void;
  onDisband: () => void;
  onOpenRally: () => void;
  onOpenResearch: () => void;
  onOpenContribute: () => void;
  onOpenContributors: () => void;
  onOpenBuildings: () => void;
  errorMsg: string | null;
}) {
  const isLeader = leaderId === myId;
  return (
    <div className="flex h-full flex-col">
      <div className="px-3 py-2">
        <div className="rounded-xl bg-tg-secondaryBg p-3">
          <div className="flex items-baseline justify-between">
            <div className="text-lg font-semibold">[{tag}] {name}</div>
            <div className="text-xs text-tg-hint">
              {members.length}/50
            </div>
          </div>
          {description && (
            <div className="mt-1 text-sm text-tg-hint">{description}</div>
          )}
        </div>
      </div>

      <div className="px-3 pb-2">
        <button
          type="button"
          onClick={() => {
            hapticImpact("medium");
            onOpenRally();
          }}
          className="w-full rounded-lg bg-tg-button px-4 py-3 font-medium text-tg-buttonText"
        >
          Ралли
        </button>
      </div>

      <div className="grid grid-cols-2 gap-2 px-3 pb-2">
        <button
          type="button"
          onClick={onOpenResearch}
          className="rounded-lg bg-tg-secondaryBg px-3 py-2 text-sm"
        >
          Исследования
        </button>
        <button
          type="button"
          onClick={onOpenBuildings}
          className="rounded-lg bg-tg-secondaryBg px-3 py-2 text-sm"
        >
          Здания
        </button>
      </div>
      <div className="grid grid-cols-2 gap-2 px-3 pb-3">
        <button
          type="button"
          onClick={onOpenContribute}
          className="rounded-lg bg-tg-secondaryBg px-3 py-2 text-sm"
        >
          Внести
        </button>
        <button
          type="button"
          onClick={onOpenContributors}
          className="rounded-lg bg-tg-secondaryBg px-3 py-2 text-sm"
        >
          Топ донат
        </button>
      </div>

      <div className="px-3 pb-1 text-xs uppercase tracking-wide text-tg-hint">
        Состав
      </div>

      <div className="flex-1 space-y-2 overflow-y-auto px-3 pb-3">
        {members.map((m) => (
          <div
            key={m.playerId}
            className="flex items-center gap-2 rounded-xl bg-tg-secondaryBg p-2.5"
          >
            <ElementEmblem element={m.element} size={28} />
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium">{m.displayName}</div>
              <div className="text-xs text-tg-hint">
                {ElementRu[m.element]} · {m.role === "leader" ? (<span className="inline-flex items-center gap-0.5"><GiCrown size={12} /> Лидер</span>) : "Член"}
              </div>
            </div>
            {isLeader && m.playerId !== myId && (
              <button
                type="button"
                onClick={() => onKick(m.playerId)}
                className="rounded-lg bg-tg-bg px-2 py-1 text-xs text-tg-destructive"
              >
                Выкинуть
              </button>
            )}
          </div>
        ))}

        {errorMsg && (
          <div className="rounded-lg bg-tg-destructive/20 p-2 text-sm text-tg-destructive">
            {errorMsg}
          </div>
        )}

        <div className="pt-2">
          {isLeader ? (
            <button
              type="button"
              onClick={() => {
                if (confirm("Распустить альянс?")) onDisband();
              }}
              className="w-full rounded-lg bg-tg-secondaryBg px-4 py-2 text-sm text-tg-destructive"
            >
              <span className="inline-flex items-center justify-center gap-1.5"><GiBreakingChain size={14} /> Распустить альянс</span>
            </button>
          ) : (
            <button
              type="button"
              onClick={onLeave}
              className="w-full rounded-lg bg-tg-secondaryBg px-4 py-2 text-sm text-tg-destructive"
            >
              ↩ Выйти из альянса
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function DirectoryView({
  alliances,
  onBack,
  onJoin,
  joinPending,
  errorMsg,
}: {
  alliances: { id: string; tag: string; name: string; memberCount: number; description: string }[];
  onBack: () => void;
  onJoin: (id: string) => void;
  joinPending: boolean;
  errorMsg: string | null;
}) {
  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-2 px-3 py-2">
        <button
          type="button"
          onClick={onBack}
          className="rounded-lg bg-tg-secondaryBg px-3 py-1.5 text-sm"
        >
          ←
        </button>
        <div className="font-medium inline-flex items-center gap-1.5"><GiMagnifyingGlass size={14} /> Альянсы мира</div>
      </div>

      {errorMsg && (
        <div className="mx-3 mb-2 rounded-lg bg-tg-destructive/20 p-2 text-sm text-tg-destructive">
          {errorMsg}
        </div>
      )}

      <div className="flex-1 space-y-2 overflow-y-auto px-3 pb-3">
        {alliances.length === 0 ? (
          <div className="rounded-xl bg-tg-secondaryBg p-4 text-center text-sm text-tg-hint">
            Альянсов пока нет. Будь первым.
          </div>
        ) : (
          alliances.map((a) => (
            <div key={a.id} className="rounded-xl bg-tg-secondaryBg p-3">
              <div className="flex items-baseline justify-between">
                <div className="font-medium">
                  [{a.tag}] {a.name}
                </div>
                <div className="text-xs text-tg-hint">{a.memberCount}/50</div>
              </div>
              {a.description && (
                <div className="mt-1 line-clamp-2 text-xs text-tg-hint">
                  {a.description}
                </div>
              )}
              <button
                type="button"
                disabled={joinPending}
                onClick={() => onJoin(a.id)}
                className="mt-2 w-full rounded-lg bg-tg-button px-3 py-1.5 text-sm text-tg-buttonText disabled:opacity-40"
              >
                Вступить
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function CreateAllianceForm({
  value,
  onChange,
  onBack,
  onSubmit,
  pending,
  errorMsg,
}: {
  value: { tag: string; name: string; description: string };
  onChange: (v: { tag: string; name: string; description: string }) => void;
  onBack: () => void;
  onSubmit: () => void;
  pending: boolean;
  errorMsg: string | null;
}) {
  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-2 px-3 py-2">
        <button
          type="button"
          onClick={onBack}
          className="rounded-lg bg-tg-secondaryBg px-3 py-1.5 text-sm"
        >
          ←
        </button>
        <div className="font-medium inline-flex items-center gap-1.5"><span className="text-base leading-none">+</span> Создать альянс</div>
      </div>

      <div className="flex-1 space-y-3 overflow-y-auto px-3 pb-3">
        <Field
          label="Тег (2–5 заглавных латиницы/цифр)"
          value={value.tag}
          onChange={(v) =>
            onChange({ ...value, tag: v.toUpperCase().slice(0, 5).replace(/[^A-Z0-9]/g, "") })
          }
          placeholder="ELEM"
        />
        <Field
          label="Название (3–40 символов)"
          value={value.name}
          onChange={(v) => onChange({ ...value, name: v.slice(0, 40) })}
          placeholder="Властители стихий"
        />
        <Field
          label="Описание"
          value={value.description}
          onChange={(v) => onChange({ ...value, description: v.slice(0, 200) })}
          placeholder="Краткий девиз"
          multiline
        />

        {errorMsg && (
          <div className="rounded-lg bg-tg-destructive/20 p-2 text-sm text-tg-destructive">
            {errorMsg}
          </div>
        )}

        <button
          type="button"
          disabled={pending || !value.tag || !value.name}
          onClick={onSubmit}
          className="w-full rounded-lg bg-tg-button px-4 py-3 font-medium text-tg-buttonText disabled:opacity-40"
        >
          {pending ? "Создаю…" : "Создать"}
        </button>
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  placeholder,
  multiline,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  multiline?: boolean;
}) {
  return (
    <label className="block">
      <div className="mb-1 text-xs text-tg-hint">{label}</div>
      {multiline ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          rows={3}
          className="w-full rounded-lg bg-tg-secondaryBg p-2 text-sm"
        />
      ) : (
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full rounded-lg bg-tg-secondaryBg p-2 text-sm"
        />
      )}
    </label>
  );
}

function RallyHome({
  activeRallies,
  camps,
  now,
  onBack,
  onPickCamp,
  onPickRally,
  errorMsg,
}: {
  activeRallies: { id: string; campName: string | null; windowEndsAt: string; participantCount: number; totalPower: number }[];
  camps: RallyCampEntry[];
  now: Date;
  onBack: () => void;
  onPickCamp: (c: RallyCampEntry) => void;
  onPickRally: (id: string) => void;
  errorMsg: string | null;
}) {
  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-2 px-3 py-2">
        <button
          type="button"
          onClick={onBack}
          className="rounded-lg bg-tg-secondaryBg px-3 py-1.5 text-sm"
        >
          ←
        </button>
        <div className="font-medium">Ралли</div>
      </div>

      {errorMsg && (
        <div className="mx-3 mb-2 rounded-lg bg-tg-destructive/20 p-2 text-sm text-tg-destructive">
          {errorMsg}
        </div>
      )}

      <div className="flex-1 space-y-3 overflow-y-auto px-3 pb-3">
        {activeRallies.length > 0 && (
          <div>
            <div className="mb-1 text-xs uppercase tracking-wide text-tg-hint">
              Идёт сейчас
            </div>
            <div className="space-y-2">
              {activeRallies.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => onPickRally(r.id)}
                  className="flex w-full items-center justify-between rounded-xl bg-tg-secondaryBg p-3 text-left"
                >
                  <div>
                    <div className="font-medium">{r.campName ?? "Rally"}</div>
                    <div className="text-xs text-tg-hint">
                      {r.participantCount} в сборе · сила {formatNumber(r.totalPower)}
                    </div>
                  </div>
                  <div className="text-xs tabular-nums text-tg-accent">
                    {formatDuration(secondsUntil(r.windowEndsAt, now))}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        <div>
          <div className="mb-1 text-xs uppercase tracking-wide text-tg-hint">
            Логова боссов
          </div>
          <div className="space-y-2">
            {camps.map((c) => {
              const cooldownLeft = c.nextAttackAt ? secondsUntil(c.nextAttackAt, now) : 0;
              const disabled = c.onCooldown && cooldownLeft > 0;
              return (
                <button
                  key={c.id}
                  type="button"
                  disabled={disabled}
                  onClick={() => onPickCamp(c)}
                  className="flex w-full items-center gap-3 rounded-xl bg-tg-secondaryBg p-3 text-left disabled:opacity-50"
                >
                  <TierShield tier={c.tier} size={48} className="shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="font-medium">{c.name}</div>
                    <div className="line-clamp-1 text-xs text-tg-hint">{c.description}</div>
                    <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-tg-hint">
                      <CurrencyValue kind="gold" value={formatNumber(c.loot.gold)} />
                      <CurrencyValue kind="xp" value={formatNumber(c.heroXp)} />
                      <CurrencyValue kind="shards" value={formatNumber(c.soulShards)} />
                      <span>· сбор {formatDuration(c.gatheringSeconds)}</span>
                    </div>
                    {disabled && (
                      <div className="mt-1 text-xs text-tg-accent">
                        {formatDuration(cooldownLeft)} cooldown
                      </div>
                    )}
                  </div>
                  <div className="text-tg-hint">›</div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function RallyDetailsView({
  details,
  now,
  myId,
  onBack,
  onCancel,
  onJoin,
  errorMsg,
}: {
  details: import("../types/rally").RallyDetails | null;
  now: Date;
  myId: string;
  onBack: () => void;
  onCancel: () => void;
  onJoin: () => void;
  errorMsg: string | null;
}) {
  if (!details) return <Loading message="Гружу ралли..." />;

  const isLeader = details.leaderId === myId;
  const isParticipant = details.participants.some((p) => p.playerId === myId);
  const isGathering = details.status === "gathering";
  const remaining = secondsUntil(details.windowEndsAt, now);

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-2 px-3 py-2">
        <button
          type="button"
          onClick={onBack}
          className="rounded-lg bg-tg-secondaryBg px-3 py-1.5 text-sm"
        >
          ←
        </button>
        <div className="font-medium">{details.campName ?? "Rally"}</div>
      </div>

      <div className="px-3 pb-2 text-xs text-tg-hint">
        Статус: <b>{details.status}</b>
        {isGathering && ` · ${formatDuration(remaining)}`}
        {details.outcome && ` · итог: ${details.outcome}`}
      </div>

      {errorMsg && (
        <div className="mx-3 mb-2 rounded-lg bg-tg-destructive/20 p-2 text-sm text-tg-destructive">
          {errorMsg}
        </div>
      )}

      <div className="flex-1 space-y-2 overflow-y-auto px-3 pb-3">
        <div className="mb-1 text-xs uppercase tracking-wide text-tg-hint">
          Участники ({details.participants.length})
        </div>
        {details.participants.map((p) => (
          <div
            key={p.playerId}
            className={`rounded-xl bg-tg-secondaryBg p-2.5 ${
              p.playerId === details.leaderId ? "border border-tg-accent" : ""
            }`}
          >
            <div className="flex items-center justify-between text-sm">
              <div className="font-medium">
                {p.playerId === details.leaderId ? "Лидер · " : ""}
                {p.displayName ?? p.playerId.slice(0, 8)}
              </div>
              <div className="tabular-nums text-tg-hint">
                Сила {formatNumber(p.power)}
              </div>
            </div>
            {(p.loot || p.heroXp || p.soulShards) && (
              <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-tg-hint">
                {p.loot && (
                  <>
                    <CurrencyValue kind="gold" value={formatNumber(p.loot.gold)} />
                    <CurrencyValue kind="wood" value={formatNumber(p.loot.wood)} />
                    <CurrencyValue kind="stone" value={formatNumber(p.loot.stone)} />
                    <CurrencyValue kind="mana" value={formatNumber(p.loot.mana)} />
                  </>
                )}
                {p.heroXp ? (
                  <>
                    <span>·</span>
                    <CurrencyValue kind="xp" value={formatNumber(p.heroXp)} />
                  </>
                ) : null}
                {p.soulShards ? (
                  <>
                    <span>·</span>
                    <CurrencyValue kind="shards" value={formatNumber(p.soulShards)} />
                  </>
                ) : null}
              </div>
            )}
          </div>
        ))}
      </div>

      {isGathering && (
        <div className="space-y-2 border-t border-tg-secondaryBg p-3">
          {!isParticipant && (
            <button
              type="button"
              onClick={onJoin}
              className="w-full rounded-lg bg-tg-button px-4 py-3 font-medium text-tg-buttonText"
            >
              <span className="inline-flex items-center justify-center gap-1.5"><GiCheckMark size={14} /> Присоединиться</span>
            </button>
          )}
          {isLeader && (
            <button
              type="button"
              onClick={onCancel}
              className="w-full rounded-lg bg-tg-secondaryBg px-4 py-2 text-sm text-tg-destructive"
            >
              Отменить ралли
            </button>
          )}
        </div>
      )}
    </div>
  );
}

type AllianceSectionKey = "Development" | "Economy" | "Military";

const SECTION_LABELS: Record<AllianceSectionKey, string> = {
  Development: "Развитие",
  Economy: "Экономика",
  Military: "Военное дело",
};

const SECTION_ORDER: AllianceSectionKey[] = ["Development", "Economy", "Military"];

// Human-readable suffix for the rank-effect cell. The catalog enum mirrors
// AllianceMagnitudeProfile in the server. Member cap is integer slots, the
// rest are percentages.
function formatTreeEffect(tree: AllianceResearchTreeView, value: number): string {
  if (tree.magnitude === "MemberCap") {
    const slots = Math.round(value);
    return slots === 0 ? "—" : `+${slots} мест`;
  }
  const pct = value * 100;
  if (pct === 0) return "—";
  const sign = pct > 0 ? "+" : "";
  return `${sign}${pct.toFixed(pct >= 10 ? 0 : pct >= 1 ? 1 : 2)}%`;
}

// Tier label for the current rank — Lords-Mobile style "Bronze/Silver/Gold/Master".
function tierLabel(rank: number, maxRank: number): string {
  if (rank <= 0) return "—";
  if (rank > maxRank) return "max";
  const tier = Math.floor((rank - 1) / 5) + 1;
  return `T${tier} R${((rank - 1) % 5) + 1}`;
}

function AllianceResearchView({
  data,
  isLoading,
  isLeader,
  now,
  onBack,
  onStart,
  startPending,
  startError,
}: {
  data: AllianceResearchResponse | undefined;
  isLoading: boolean;
  isLeader: boolean;
  now: Date;
  onBack: () => void;
  onStart: (treeId: string) => void;
  startPending: boolean;
  startError: string | null;
}) {
  const [section, setSection] = useState<AllianceSectionKey>("Development");

  if (isLoading || !data) return <Loading message="Гружу исследования..." />;

  const treasury = data.treasury;
  const active = data.active;
  const activeTree = active ? data.trees.find((t) => t.id === active.treeId) : null;
  const remaining = active ? secondsUntil(active.finishesAt, now) : 0;
  const someoneActive = active !== null;

  function canAfford(cost: { gold: number; wood: number; stone: number; mana: number }) {
    return (
      treasury.gold >= cost.gold &&
      treasury.wood >= cost.wood &&
      treasury.stone >= cost.stone &&
      treasury.mana >= cost.mana
    );
  }

  const sectionTrees = data.trees.filter((t) => t.section === section);

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-2 px-3 py-2">
        <button
          type="button"
          onClick={onBack}
          className="rounded-lg bg-tg-secondaryBg px-3 py-1.5 text-sm"
        >
          ←
        </button>
        <div className="font-medium">Исследования альянса</div>
      </div>

      <div className="px-3 pb-2">
        <div className="rounded-xl bg-tg-secondaryBg p-3">
          <div className="mb-1 text-xs uppercase tracking-wide text-tg-hint">
            Казна
          </div>
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <CurrencyValue kind="gold" value={formatNumber(treasury.gold)} />
            <CurrencyValue kind="wood" value={formatNumber(treasury.wood)} />
            <CurrencyValue kind="stone" value={formatNumber(treasury.stone)} />
            <CurrencyValue kind="mana" value={formatNumber(treasury.mana)} />
          </div>
        </div>
      </div>

      {active && activeTree && (
        <div className="px-3 pb-2">
          <div className="rounded-xl border border-tg-accent bg-tg-secondaryBg p-3">
            <div className="text-xs uppercase tracking-wide text-tg-accent">
              В работе
            </div>
            <div className="mt-1 font-medium">
              {activeTree.name} · ранг {active.targetRank}/{activeTree.maxRank}
            </div>
            <div className="text-xs text-tg-hint">{activeTree.description}</div>
            <div className="mt-2 text-xs tabular-nums text-tg-accent">
              Осталось {formatDuration(remaining)}
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-3 gap-1.5 px-3 pb-2">
        {SECTION_ORDER.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setSection(s)}
            className={`rounded-lg px-2 py-1.5 text-xs ${
              section === s
                ? "bg-tg-button text-tg-buttonText"
                : "bg-tg-secondaryBg text-tg-text"
            }`}
          >
            {SECTION_LABELS[s]}
          </button>
        ))}
      </div>

      {startError && (
        <div className="mx-3 mb-2 rounded-lg bg-tg-destructive/20 p-2 text-sm text-tg-destructive">
          {startError}
        </div>
      )}

      <div className="flex-1 space-y-2 overflow-y-auto px-3 pb-3">
        {sectionTrees.map((tree) => {
          const isMaxed = tree.currentRank >= tree.maxRank;
          const isActiveTree = active?.treeId === tree.id;
          const affordable = tree.nextCost !== null && canAfford(tree.nextCost);
          const disabled =
            !isLeader ||
            isMaxed ||
            isActiveTree ||
            !affordable ||
            startPending ||
            someoneActive;

          return (
            <div
              key={tree.id}
              className={`rounded-xl bg-tg-secondaryBg p-3 ${
                isMaxed ? "opacity-60" : ""
              }`}
            >
              <div className="flex items-baseline justify-between gap-2">
                <div className="font-medium">{tree.name}</div>
                <div className="text-xs tabular-nums text-tg-hint">
                  {tree.currentRank}/{tree.maxRank} · {tierLabel(tree.currentRank, tree.maxRank)}
                </div>
              </div>
              <div className="mt-0.5 text-xs text-tg-hint">{tree.description}</div>

              <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-tg-bg">
                <div
                  className="h-full bg-tg-accent"
                  style={{ width: `${(tree.currentRank / tree.maxRank) * 100}%` }}
                />
              </div>

              <div className="mt-2 flex flex-wrap items-baseline gap-3 text-xs">
                <span className="text-tg-hint">
                  Сейчас:&nbsp;
                  <span className="text-tg-accent">
                    {formatTreeEffect(tree, tree.currentEffectPct)}
                  </span>
                </span>
                {!isMaxed && (
                  <span className="text-tg-hint">
                    След.:&nbsp;
                    <span className="text-tg-text">
                      {formatTreeEffect(tree, tree.nextEffectStepPct)}
                    </span>
                  </span>
                )}
              </div>

              {!isMaxed && tree.nextCost && tree.nextDurationSeconds !== null && (
                <>
                  <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                    <CurrencyValue kind="gold" value={formatNumber(tree.nextCost.gold)} />
                    <CurrencyValue kind="wood" value={formatNumber(tree.nextCost.wood)} />
                    <CurrencyValue kind="stone" value={formatNumber(tree.nextCost.stone)} />
                    <CurrencyValue kind="mana" value={formatNumber(tree.nextCost.mana)} />
                    <span className="text-tg-hint">
                      · {formatDuration(tree.nextDurationSeconds)}
                    </span>
                  </div>

                  <button
                    type="button"
                    disabled={disabled}
                    onClick={() => onStart(tree.id)}
                    className="mt-2 w-full rounded-lg bg-tg-button px-3 py-1.5 text-sm text-tg-buttonText disabled:opacity-40"
                  >
                    {isActiveTree
                      ? "В работе"
                      : !isLeader
                        ? "Только лидер"
                        : !affordable
                          ? "Не хватает в казне"
                          : someoneActive
                            ? "Уже идёт другое"
                            : startPending
                              ? "Запускаю…"
                              : `Запустить ранг ${tree.nextRank}`}
                  </button>
                </>
              )}

              {isMaxed && (
                <div className="mt-2 inline-flex items-center gap-1 text-xs text-tg-accent">
                  <GiCheckMark size={12} /> максимум
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AllianceContributeView({
  resources,
  treasury,
  onBack,
  onSubmit,
  pending,
  errorMsg,
}: {
  resources: { gold: number; wood: number; stone: number; mana: number };
  treasury: { gold: number; wood: number; stone: number; mana: number } | undefined;
  onBack: () => void;
  onSubmit: (amounts: { gold: number; wood: number; stone: number; mana: number }) => void;
  pending: boolean;
  errorMsg: string | null;
}) {
  const [amounts, setAmounts] = useState({ gold: 0, wood: 0, stone: 0, mana: 0 });
  const kinds: Array<{ key: "gold" | "wood" | "stone" | "mana"; label: string }> = [
    { key: "gold", label: "Золото" },
    { key: "wood", label: "Дерево" },
    { key: "stone", label: "Камень" },
    { key: "mana", label: "Мана" },
  ];

  const total = amounts.gold + amounts.wood + amounts.stone + amounts.mana;
  const valid =
    total > 0 &&
    amounts.gold <= resources.gold &&
    amounts.wood <= resources.wood &&
    amounts.stone <= resources.stone &&
    amounts.mana <= resources.mana &&
    amounts.gold >= 0 &&
    amounts.wood >= 0 &&
    amounts.stone >= 0 &&
    amounts.mana >= 0;

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-2 px-3 py-2">
        <button
          type="button"
          onClick={onBack}
          className="rounded-lg bg-tg-secondaryBg px-3 py-1.5 text-sm"
        >
          ←
        </button>
        <div className="font-medium">Внести в казну</div>
      </div>

      {treasury && (
        <div className="px-3 pb-2">
          <div className="rounded-xl bg-tg-secondaryBg p-3">
            <div className="mb-1 text-xs uppercase tracking-wide text-tg-hint">
              Казна сейчас
            </div>
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <CurrencyValue kind="gold" value={formatNumber(treasury.gold)} />
              <CurrencyValue kind="wood" value={formatNumber(treasury.wood)} />
              <CurrencyValue kind="stone" value={formatNumber(treasury.stone)} />
              <CurrencyValue kind="mana" value={formatNumber(treasury.mana)} />
            </div>
          </div>
        </div>
      )}

      <div className="flex-1 space-y-3 overflow-y-auto px-3 pb-3">
        {kinds.map((k) => {
          const have = resources[k.key];
          const value = amounts[k.key];
          return (
            <div key={k.key} className="rounded-xl bg-tg-secondaryBg p-3">
              <div className="mb-1 flex items-baseline justify-between text-sm">
                <span>{k.label}</span>
                <span className="text-xs text-tg-hint">
                  у тебя {formatNumber(have)}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min={0}
                  max={have}
                  value={value === 0 ? "" : value}
                  onChange={(e) => {
                    const raw = Number(e.target.value);
                    const clamped = Math.max(0, Math.min(have, Number.isFinite(raw) ? raw : 0));
                    setAmounts({ ...amounts, [k.key]: clamped });
                  }}
                  className="flex-1 rounded-lg bg-tg-bg p-2 text-sm tabular-nums"
                  placeholder="0"
                />
                <button
                  type="button"
                  onClick={() => setAmounts({ ...amounts, [k.key]: Math.floor(have / 10) })}
                  className="rounded-lg bg-tg-bg px-2 py-2 text-xs"
                >
                  10%
                </button>
                <button
                  type="button"
                  onClick={() => setAmounts({ ...amounts, [k.key]: have })}
                  className="rounded-lg bg-tg-bg px-2 py-2 text-xs"
                >
                  всё
                </button>
              </div>
            </div>
          );
        })}

        {errorMsg && (
          <div className="rounded-lg bg-tg-destructive/20 p-2 text-sm text-tg-destructive">
            {errorMsg}
          </div>
        )}

        <button
          type="button"
          disabled={pending || !valid}
          onClick={() => onSubmit(amounts)}
          className="w-full rounded-lg bg-tg-button px-4 py-3 font-medium text-tg-buttonText disabled:opacity-40"
        >
          {pending ? "Отправляю…" : "Внести"}
        </button>
      </div>
    </div>
  );
}

function AllianceContributorsView({
  contributors,
  onBack,
}: {
  contributors: AllianceContributor[];
  onBack: () => void;
}) {
  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-2 px-3 py-2">
        <button
          type="button"
          onClick={onBack}
          className="rounded-lg bg-tg-secondaryBg px-3 py-1.5 text-sm"
        >
          ←
        </button>
        <div className="font-medium">Топ доната</div>
      </div>

      <div className="flex-1 space-y-2 overflow-y-auto px-3 pb-3">
        {contributors.length === 0 ? (
          <div className="rounded-xl bg-tg-secondaryBg p-4 text-center text-sm text-tg-hint">
            Пока никто не вносил в казну.
          </div>
        ) : (
          contributors.map((c, i) => (
            <div
              key={c.playerId}
              className="flex items-center gap-3 rounded-xl bg-tg-secondaryBg p-3"
            >
              <div className="w-6 text-center text-sm tabular-nums text-tg-hint">
                {i + 1}
              </div>
              <div className="min-w-0 flex-1 truncate text-sm font-medium">
                {c.name}
              </div>
              <div className="text-sm tabular-nums text-tg-accent">
                {formatNumber(c.total)}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function AllianceBuildingsView({
  data,
  isLoading,
  isLeader,
  now,
  onBack,
  onStart,
  startPending,
  startError,
}: {
  data: AllianceBuildingsResponse | undefined;
  isLoading: boolean;
  isLeader: boolean;
  now: Date;
  onBack: () => void;
  onStart: (buildingId: string) => void;
  startPending: boolean;
  startError: string | null;
}) {
  if (isLoading || !data) return <Loading message="Гружу здания альянса..." />;

  const treasury = data.treasury;
  const active = data.active;
  const activeBld = active ? data.buildings.find((b) => b.id === active.buildingId) : null;
  const remaining = active ? secondsUntil(active.finishesAt, now) : 0;
  const someoneActive = active !== null;

  function canAfford(cost: { gold: number; wood: number; stone: number; mana: number }) {
    return (
      treasury.gold >= cost.gold &&
      treasury.wood >= cost.wood &&
      treasury.stone >= cost.stone &&
      treasury.mana >= cost.mana
    );
  }

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-2 px-3 py-2">
        <button
          type="button"
          onClick={onBack}
          className="rounded-lg bg-tg-secondaryBg px-3 py-1.5 text-sm"
        >
          ←
        </button>
        <div className="font-medium">Здания альянса</div>
      </div>

      <div className="px-3 pb-2">
        <div className="rounded-xl bg-tg-secondaryBg p-3">
          <div className="mb-1 text-xs uppercase tracking-wide text-tg-hint">
            Казна
          </div>
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <CurrencyValue kind="gold" value={formatNumber(treasury.gold)} />
            <CurrencyValue kind="wood" value={formatNumber(treasury.wood)} />
            <CurrencyValue kind="stone" value={formatNumber(treasury.stone)} />
            <CurrencyValue kind="mana" value={formatNumber(treasury.mana)} />
          </div>
        </div>
      </div>

      {active && activeBld && (
        <div className="px-3 pb-2">
          <div className="rounded-xl border border-tg-accent bg-tg-secondaryBg p-3">
            <div className="text-xs uppercase tracking-wide text-tg-accent">
              В постройке
            </div>
            <div className="mt-1 font-medium">
              {activeBld.name} · Lvl {active.targetLevel}/{activeBld.maxLevel}
            </div>
            <div className="text-xs text-tg-hint">{activeBld.description}</div>
            <div className="mt-2 text-xs tabular-nums text-tg-accent">
              Осталось {formatDuration(remaining)}
            </div>
          </div>
        </div>
      )}

      {startError && (
        <div className="mx-3 mb-2 rounded-lg bg-tg-destructive/20 p-2 text-sm text-tg-destructive">
          {startError}
        </div>
      )}

      <div className="flex-1 space-y-2 overflow-y-auto px-3 pb-3">
        {data.buildings.map((bld) => {
          const isMaxed = bld.currentLevel >= bld.maxLevel;
          const isActiveBld = active?.buildingId === bld.id;
          const affordable = bld.nextCost !== null && canAfford(bld.nextCost);
          const disabled =
            !isLeader || isMaxed || isActiveBld || !affordable || startPending || someoneActive;

          const currentPct = bld.currentEffectPct * 100;
          const nextPct = bld.nextEffectStepPct * 100;

          return (
            <div
              key={bld.id}
              className={`rounded-xl bg-tg-secondaryBg p-3 ${isMaxed ? "opacity-60" : ""}`}
            >
              <div className="flex items-baseline justify-between gap-2">
                <div className="font-medium">{bld.name}</div>
                <div className="text-xs tabular-nums text-tg-hint">
                  Lvl {bld.currentLevel}/{bld.maxLevel}
                </div>
              </div>
              <div className="mt-0.5 text-xs text-tg-hint">{bld.description}</div>

              <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-tg-bg">
                <div
                  className="h-full bg-tg-accent"
                  style={{ width: `${(bld.currentLevel / bld.maxLevel) * 100}%` }}
                />
              </div>

              <div className="mt-2 flex flex-wrap items-baseline gap-3 text-xs">
                <span className="text-tg-hint">
                  Сейчас:&nbsp;
                  <span className="text-tg-accent">
                    {currentPct === 0 ? "—" : `+${currentPct.toFixed(currentPct >= 10 ? 0 : 1)}%`}
                  </span>
                </span>
                {!isMaxed && (
                  <span className="text-tg-hint">
                    След.:&nbsp;
                    <span className="text-tg-text">
                      +{nextPct.toFixed(nextPct >= 10 ? 0 : 1)}%
                    </span>
                  </span>
                )}
              </div>

              {!isMaxed && bld.nextCost && bld.nextDurationSeconds !== null && (
                <>
                  <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                    <CurrencyValue kind="gold" value={formatNumber(bld.nextCost.gold)} />
                    <CurrencyValue kind="wood" value={formatNumber(bld.nextCost.wood)} />
                    <CurrencyValue kind="stone" value={formatNumber(bld.nextCost.stone)} />
                    <CurrencyValue kind="mana" value={formatNumber(bld.nextCost.mana)} />
                    <span className="text-tg-hint">
                      · {formatDuration(bld.nextDurationSeconds)}
                    </span>
                  </div>

                  <button
                    type="button"
                    disabled={disabled}
                    onClick={() => onStart(bld.id)}
                    className="mt-2 w-full rounded-lg bg-tg-button px-3 py-1.5 text-sm text-tg-buttonText disabled:opacity-40"
                  >
                    {isActiveBld
                      ? "В постройке"
                      : !isLeader
                        ? "Только лидер"
                        : !affordable
                          ? "Не хватает в казне"
                          : someoneActive
                            ? "Уже идёт другое"
                            : startPending
                              ? "Запускаю…"
                              : `Построить Lvl ${bld.nextLevel}`}
                  </button>
                </>
              )}

              {isMaxed && (
                <div className="mt-2 inline-flex items-center gap-1 text-xs text-tg-accent">
                  <GiCheckMark size={12} /> максимум
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function pickError(err: unknown): string | null {
  if (!err || typeof err !== "object") return null;
  const resp = (err as { response?: { data?: { error?: string } } }).response;
  return resp?.data?.error ?? null;
}

